import { CommonModule } from '@angular/common';
import { Component, ContentChildren, QueryList, OnInit, OnDestroy, Input, Output, EventEmitter, computed, effect, signal, TemplateRef, AfterViewInit, ChangeDetectorRef, SimpleChanges } from '@angular/core';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { StepperStepDirective } from '../stepper-layout/stepper-step.directive';
import { StepperService } from '../stepper-layout/stepper.service';
import { StepperConfig, StepperStepChangeEvent } from '../stepper-layout/stepper.types';
import { IconComponent } from '../../../@core/components/ui/icon/icon.component';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { SanitizeHtmlPipe } from '../../pipes/sanitizeHtml.pipe';
import { ToastService } from '../../../@core/services/toast.service';

/**
 * Stepper Component
 * 
 * A reusable multi-step stepper component with progress tracking.
 * Follows industry-standard patterns from Angular Material Stepper.
 * 
 * Features:
 * - Two-column layout (left: progress tracker, right: step content)
 * - Dynamic step rendering via directives
 * - State management via scoped StepperService
 * - Validation handled by parent/step templates
 * - Buttons are part of step content (not in footer)
 * 
 * @example
 * ```html
 * <app-stepper [config]="stepperConfig" [visible]="showStepper">
 *   <div *stepperStep="step0Config">
 *     <!-- Step 0 content with buttons -->
 *   </div>
 *   <div *stepperStep="step1Config">
 *     <!-- Step 1 content with buttons -->
 *   </div>
 * </app-stepper>
 * ```
 */
@Component({
    selector: 'app-stepper',
    standalone: true,
    imports: [
        CommonModule,
        DialogComponent,
        IconComponent,
        ButtonComponent,
        SanitizeHtmlPipe
    ],
    providers: [StepperService],
    templateUrl: './stepper.component.html',
    styleUrls: ['./stepper.component.scss']
})
export class StepperComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input({ required: true }) config!: StepperConfig;
    @Input() visible: boolean = false;

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() stepChange = new EventEmitter<StepperStepChangeEvent>();
    @Output() finish = new EventEmitter<void>();
    @Output() close = new EventEmitter<void>();
    @Output() closeRequested = new EventEmitter<void>();
    @Output() nextStepClick = new EventEmitter<void>();
    @Output() previousStepClick = new EventEmitter<void>();
    @Output() skipStepClick = new EventEmitter<void>();

    @Input() disableNextButton: boolean = false;
    @Input() finishButtonLoading: boolean = false;
    @Input() interceptClose: boolean = false;

    @ContentChildren(StepperStepDirective) stepDirectives!: QueryList<StepperStepDirective>;

    // Internal state
    private stepTemplates = new Map<number, TemplateRef<any>>();
    private previousStepIndex = 0;
    private stepTemplatesVersion = signal(0);

    // Computed values
    readonly currentStepTemplate = computed(() => {
        this.stepTemplatesVersion();
        const currentIndex = this.stepperService.currentStepIndex();
        return this.stepTemplates.get(currentIndex);
    });

    readonly currentStepImage = computed(() => {
        const currentStep = this.stepperService.currentStep();
        return currentStep?.svg;
    });

    readonly stepsAwayText = computed(() => {
        const remaining = this.stepperService.remainingSteps();
        return `You're just ${remaining} step${remaining !== 1 ? 's' : ''} away!`;
    });

    readonly stepStatus = computed(() => {
        const currentIndex = this.stepperService.currentStepIndex();
        const totalSteps = this.stepperService.totalSteps();
        const steps = this.stepperService.steps();

        return steps.map((step, index) => {
            if (index < currentIndex) {
                return { ...step, status: 'completed' as const };
            } else if (index === currentIndex) {
                return { ...step, status: 'current' as const };
            } else {
                return { ...step, status: 'pending' as const };
            }
        });
    });

    readonly isCurrentStepSkipable = computed(() => {
        const currentStep = this.stepperService.currentStep();
        return currentStep?.skipable === true;
    });

    constructor(
        public stepperService: StepperService,
        private cdr: ChangeDetectorRef,
        private toast: ToastService
    ) {
        // Emit step change events - optimized to only track currentStepIndex
        effect(() => {
            const currentIndex = this.stepperService.currentStepIndex();
            // Only emit if the index actually changed
            if (currentIndex !== this.previousStepIndex) {
                const currentStep = this.stepperService.currentStep();
                if (currentStep) {
                    if (currentIndex > this.previousStepIndex) {
                        this.nextStepClick.emit();
                    }
                    this.stepChange.emit({
                        previousIndex: this.previousStepIndex,
                        currentIndex,
                        stepId: currentStep.id
                    });
                    this.previousStepIndex = currentIndex;
                }
            }
        });
    }

    ngOnInit(): void {
        this.syncConfigToService();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['config'] && !changes['config'].firstChange) {
            this.syncConfigToService();
        }
    }

    OnChanges(changes: SimpleChanges): void {
        if (changes['config'] && !changes['config'].firstChange) {
            this.syncConfigToService();
        }
    }

    private syncConfigToService(): void {
        if (this.config) {
            this.stepperService.steps.set(this.config.steps);
            const initialIndex = this.config.currentStepIndex ?? 0;
            this.stepperService.setCurrentStep(initialIndex);
            this.previousStepIndex = initialIndex;
        }
    }

    ngAfterViewInit(): void {
        this.mapStepTemplates();

        // Watch for directive changes
        this.stepDirectives.changes.subscribe(() => {
            this.mapStepTemplates();
        });
    }

    ngOnDestroy(): void {
        this.stepperService.reset();
    }

    /**
     * Map step directives to templates by index
     */
    private mapStepTemplates(): void {
        this.stepTemplates.clear();

        this.stepDirectives.forEach((directive, index) => {
            directive.stepIndex = index;
            this.stepTemplates.set(index, directive.templateRef);
        });

        this.stepTemplatesVersion.update((v) => v + 1);
        this.cdr.detectChanges();
    }

    /**
     * Handle dialog close
     */
    onClose(): void {
        if (this.interceptClose) {
            this.closeRequested.emit();
            return;
        }
        this.visible = false;
        this.visibleChange.emit(false);
        this.stepperService.reset();
        this.close.emit();
    }

    /**
     * Handle dialog hide
     */
    onHide(): void {
        this.onClose();
    }

    /**
     * Get step status class
     */
    getStepStatusClass(status: 'completed' | 'current' | 'pending'): string {
        switch (status) {
            case 'completed':
                return 'step-completed';
            case 'current':
                return 'step-current';
            case 'pending':
                return 'step-pending';
            default:
                return '';
        }
    }

    /**
     * Handle next step button click
     */
    onNextStep(): void {
        if (!this.canProceedToNext()) {
            const currentStep = this.stepperService.currentStep();
            const message = currentStep?.invalidMessage ?? 'Please complete the required fields for this step';
            this.toast.error(message);
            return;
        }
        if (!this.stepperService.isLastStep()) {
            this.stepperService.nextStep();
        } else {
            this.finish.emit();
        }
    }

    /**
     * Handle previous step button click
     */
    onPreviousStep(): void {
        this.previousStepClick.emit();
        this.stepperService.previousStep();
    }

    /**
     * Handle skip step button click
     */
    onSkipStep(): void {
        this.skipStepClick.emit();
        if (!this.stepperService.isLastStep()) {
            this.stepperService.nextStep();
        }
    }

    /**
     * Check if can proceed to next step (for validation)
     */
    canProceedToNext(): boolean {
        const currentStep = this.stepperService.currentStep();
        if (currentStep?.isValid) {
            const result = currentStep.isValid();
            return result instanceof Promise ? true : result;
        }
        return true;
    }

    /**
     * Format step number with zero padding (01, 02, 03, etc.)
     */
    formatStepNumber(index: number): string {
        return String(index + 1).padStart(2, '0');
    }

    private getFirstInvalidStepMessage(fromIndex: number, toIndex: number): string | null {
        const steps = this.stepperService.steps();
        for (let i = fromIndex; i <= toIndex; i++) {
            const step = steps[i];
            if (step?.isValid && !step.isValid()) {
                return step.invalidMessage ?? 'Please complete the required fields for this step';
            }
        }
        return null;
    }

    goToStep(index: number): void {
        if (index < 0 || index >= this.stepperService.totalSteps()) {
            return;
        }
        const currentIndex = this.stepperService.currentStepIndex();
        if (index !== currentIndex && this.disableNextButton) {
            this.toast.error('Please fill all the required fields');
            return;
        }
        if (index > currentIndex) {
            const message = this.getFirstInvalidStepMessage(currentIndex, index - 1);
            if (message) {
                this.toast.error(message);
                return;
            }
        }
        this.stepperService.goToStep(index);
    }
}
